import { Asset, checkAssetTypeValue } from '@/types/asset_search';
import { useTranslation } from 'react-i18next';
import { Box, Theme, Typography, useTheme } from '@mui/material';
import { InspectionIcon, VehicleOutlineIcon } from '../Shared/Icons';
import Can from '@/app/Auth/Can';
import { Action, Subject } from '@/types/enums';
import { useParams } from 'react-router';
import { inspectMapCode } from '@/features/mapCode/aibaMapCode';

export type AssetDetailsProps = {
  selectedAsset: Asset | null;
};

export default function AssetDetails({ selectedAsset }: AssetDetailsProps) {
  const [t] = useTranslation();
  const siteId = useParams()?.siteId?.toLowerCase() || '';
  const theme = useTheme<Theme>();

  const AssetI18nMap = new Map<keyof Asset, string>();
  AssetI18nMap.set('asset_uuid', t('assetDetail.vin'));
  AssetI18nMap.set('zone_name', t('assetDetail.zone'));
  AssetI18nMap.set('vehicle_stock_number', t('assetDetail.stockNumber'));
  AssetI18nMap.set('vehicle_make', t('assetDetail.make'));
  AssetI18nMap.set('vehicle_year', t('assetDetail.year'));
  AssetI18nMap.set(
    selectedAsset?.vehicle_model ? 'vehicle_model' : 'vehicle_model_code',
    t('assetDetail.model'),
  );
  if (
    Can(Action.READ, Subject.table_dtc) &&
    selectedAsset &&
    selectedAsset['dtc'] &&
    selectedAsset['dtc'].length > 0
  ) {
    AssetI18nMap.set('dtc', t('assetDetail.dtcCodes'));
  }
  if (Can(Action.READ, Subject.vehicle_battery)) {
    AssetI18nMap.set('vehicle_level', t('assetDetail.battery'));
  }

  if (Can(Action.READ, Subject.seller_id)) {
    AssetI18nMap.set('seller_id', t('assetDetail.sellerId'));
  }

  const InspectionI18nMap = new Map<string, string>();
  if (siteId === 'aibalpc') {
    AssetI18nMap.set('color', t('assetDetail.color'));
    AssetI18nMap.set('freeDays', t('assetDetail.freeDays'));
    AssetI18nMap.set('storageDays', t('assetDetail.storageDays'));
    AssetI18nMap.set('model', t('assetDetail.model'));
    AssetI18nMap.set('make', t('assetDetail.make'));
    AssetI18nMap.set('siteNumber', t('assetDetail.siteNumber'));

    InspectionI18nMap.set(
      'additionalServices',
      t('assetDetail.additionalServices'),
    );
    InspectionI18nMap.set('remarks', t('assetDetail.remarks'));
    const keysForAibaCode = selectedAsset?.code?.split(',');
    keysForAibaCode?.map((code) => InspectionI18nMap.set(code, code));
  }
  if (!selectedAsset) {
    return null;
  }

  const fieldsToShow = new Map(
    [...AssetI18nMap.entries()].filter(
      (asset) => selectedAsset && selectedAsset[asset[0] as keyof Asset],
    ),
  );

  const aibaCodeDescription = (code: string) => {
    const { areaValue, damageValue, severityValue } = inspectMapCode(code);
    return (
      <>
        {areaValue && areaValue} <br />
        {damageValue && damageValue} <br />
        {damageValue && severityValue}
      </>
    );
  };

  const getAssetInspectionValues = (key: any, value: any): any => {
    switch (key) {
      case 'additionalServices':
        return value;
      case 'remarks':
        return value;
      default:
        return aibaCodeDescription(key);
        break;
    }
  };

  return (
    <Box data-testid="asset-detail">
      <Box
        sx={{
          padding: '20px 16px',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <VehicleOutlineIcon
          style={{
            width: '18px',
            height: '18px',
            marginRight: '20px',
          }}
          viewBox="0 -2 20 24"
        />

        <Typography
          sx={(theme) => ({
            flex: 1,
            fontSize: '12px',
            fontWeight: '600',
            color: theme.palette.text.primary,
          })}
        >
          {t('assetDetail.vehicles')}
        </Typography>
      </Box>
      {Array.from(fieldsToShow.keys(), (asset, i) => (
        <Box
          key={asset}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            background:
              i % 2 == 0
                ? theme.palette.background.default
                : theme.palette.elevation01,
            padding: '12px 16px',
            textAlign: 'left',
          })}
        >
          <Typography
            sx={(theme) => ({
              flex: 1,
              fontSize: '12px',
              fontWeight: '600',
              color: theme.palette.text.primary,
            })}
            data-testid={`asset-detail-label-${asset}`}
          >
            {AssetI18nMap.get(asset as keyof Asset)}
          </Typography>
          <Typography
            sx={(theme) => ({
              flex: 1,
              fontSize: '12px',
              color: theme.palette.text.primary,
            })}
            data-testid={`asset-detail-value-${asset}`}
          >
            {checkAssetTypeValue(
              asset as keyof Asset,
              selectedAsset[asset as keyof Asset],
              t,
            )}
          </Typography>
        </Box>
      ))}
      {InspectionI18nMap.size > 0 && (
        <>
          <Box
            sx={(theme) => ({
              padding: '20px 16px',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              background:
                AssetI18nMap.size % 2 === 0
                  ? theme.palette.background.default
                  : theme.palette.elevation01,
              borderTop: `1px solid ${theme.palette.divider}`,
            })}
          >
            <InspectionIcon
              style={{
                width: '18px',
                height: '18px',
                marginRight: '20px',
              }}
              viewBox="0 0 20 20"
              mode={theme.palette.mode}
            />
            <Typography
              sx={(theme) => ({
                flex: 1,
                fontSize: '12px',
                fontWeight: '600',
                color: theme.palette.text.primary,
              })}
            >
              {t('assetDetail.inspection')}
            </Typography>
          </Box>
          {Array.from(InspectionI18nMap.keys(), (inspection, i) => (
            <Box
              key={inspection}
              sx={(theme) => {
                const remainder = AssetI18nMap.size % 2 === 0 ? 1 : 0;
                return {
                  display: 'flex',
                  alignItems: 'center',
                  background:
                    i % 2 === remainder
                      ? theme.palette.background.default
                      : theme.palette.elevation01,
                  padding: '12px 16px',
                  textAlign: 'left',
                };
              }}
            >
              <Typography
                sx={(theme) => ({
                  flex: 1,
                  fontSize: '12px',
                  fontWeight: '600',
                  color: theme.palette.text.primary,
                })}
                data-testid={`asset-detail-inspection-label-${inspection}`}
              >
                {InspectionI18nMap.get(inspection as keyof Asset)}
              </Typography>
              <Typography
                sx={(theme) => ({
                  flex: 1,
                  fontSize: '12px',
                  color: theme.palette.text.primary,
                })}
                data-testid={`asset-detail-inspection-value-${inspection}`}
              >
                {getAssetInspectionValues(
                  inspection as keyof Asset,
                  selectedAsset[inspection as keyof Asset],
                )}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
